<template>
  <div>
    <b-overlay
        :show="!loaded"
        no-wrap
        fixed
        z-index="996"
        opacity="0.5"
        rounded="sm"
        spinner-variant="primary"
    ></b-overlay>
    <b-alert
        :show="startPdfDownload && dismissCountDown"
        variant="success"
        dismissible
        @dismissed="dismissCountDown = 0"
        @dismiss-count-down="alertCountDownChanged"
        fade
        class="position-fixed notifications"
    >
      <b-container>
        <b-col>{{ $t("order.pdfDownloadInfo") }}</b-col>
      </b-container>
    </b-alert>
    <contact-popover :fixed="true" />
    <b-container>
      <b-row class="mt-4 mb-3">
        <b-col>
          <h3 v-html="$t('project.configurations')"></h3>
        </b-col>
        <b-col class="text-right">
          <b-button
              v-if="!disabled && loaded"
              variant="primary"
              @click="createColumnConfiguration()"
          >{{ $t("configuration.createNewConfiguration") }}</b-button
          >
        </b-col>
      </b-row>
      <b-row>
        <b-col>
          <b-alert v-if="disabled" show variant="success" class="order-alert">
            {{
              $t(`project.status.message.${$store.getters["projects/status"]}`)
            }}
          </b-alert>

          <b-table
              :items="items"
              :fields="fields"
              class="column-configuration-table"
              foot-clone
              showEmpty
              :empty-text="$t('configuration.noConfigurations')"
          >
            <template v-slot:cell(changeableAmount)="{ index }">
              <b-input
                  min="1"
                  max="1000"
                  :disabled="disabled"
                  type="number"
                  v-model.number="amounts[index]"
                  name="amount"
                  @change="changeAmount(index)"
              ></b-input>
            </template>
            <template v-slot:cell(name)="{ index }">
              <b-link
                  v-if="!disabled"
                  class="edit-name-button mr-1"
                  @click="onEditNameClick(index)"
              >
                <font-awesome-icon icon="edit" />
              </b-link>
              <router-link
                  v-if="currentlyEdited != index"
                  :to="{
                  name: 'modulePlacements',
                  params: { configurationId: index, projectId: $store.state.projects.project.number },
                }"
              >{{ names[index] }}</router-link
              >
              <input
                  v-else
                  type="text"
                  ref="nameInput"
                  :value="names[index]"
                  @keydown.enter="$event.target.blur()"
                  @blur="changeConfigurationName(index, $event.target.value)"
              />
              <div class="alert-danger">{{ errors.first("name") }}</div>
            </template>
            <template v-slot:cell(show_details)="row">
              <div
                  style="padding: 8px"
                  @click="row.toggleDetails"
                  :class="{
                  'show-details': !row.detailsShowing,
                  'show-details-open': row.detailsShowing,
                }"
              >
                <font-awesome-icon icon="caret-right" size="lg" />
              </div>
            </template>
            <template v-slot:cell(jibCrane)="row">{{
                row.item.configuration.jibCrane
                    ? $t("configuration.jibCraneWanted")
                    : $t("configuration.noJibCrane")
              }}</template>
            <template v-slot:cell(totalWeight)="{ index }">
              <span>{{
                  formatWeight((bom.entries[index] || {}).totalWeight)
                }}</span>
            </template>
            <template v-slot:cell(sum)="{ index }">
              <span>{{
                  formatPrice((bom.entries[index] || {}).totalPrice)
                }}</span>
            </template>
            <template v-slot:cell(actions)="row" v-if="!disabled">
              <b-link @click="onDeleteClick(row)">
                <font-awesome-icon icon="trash-alt" />
              </b-link>
              <span :id="'err_' + row.index">
                <font-awesome-icon
                    v-if="warnings[row.index] && warnings[row.index].length > 0"
                    icon="exclamation-triangle"
                    style="margin-left: 10px; color: orangered"
                />
              </span>
              <b-tooltip
                  placement="left"
                  customClass="my-inner-tooltip"
                  :target="'err_' + row.index"
                  variant="danger"
              >
                <div v-for="warning in warnings[row.index]" :key="warning">
                  {{
                    (2 > warnings[row.index].length ? "" : "&bull; ") +
                    $t("warnings." + warning)
                  }}
                </div>
              </b-tooltip>
            </template>

            <template v-slot:row-details="{ index }">
              <b-table-simple
                  v-if="bom"
                  small
                  style="width: calc(100% + 1.5rem); margin: -0.75rem; box-sizing: border-box;"
              >
                <b-tbody>
                  <b-tr
                      v-for="(item, i) in flatBom(
                      bom.entries ? bom.entries[index] : []
                    )"
                      :key="i"
                      :style="{
                      fontWeight: item.depth == 1 && 'bold',
                      background: `rgba(0, 0, 0, ${(item.depth - 1) * 0.05})`,
                    }"
                  >
                    <b-td :style="{ paddingLeft: item.depth * 16 + 'px' }">{{
                        item.description
                      }}</b-td>
                    <b-td class="text-right">{{ item.weight }}</b-td>
                    <b-td
                        class="text-right"
                        style="padding-right: 63px; width: 162px"
                    >{{ item.price }}</b-td
                    >
                  </b-tr>
                </b-tbody>
              </b-table-simple>
              <b-spinner
                  v-else
                  variant="primary"
                  label="Spinning"
                  style="display: block; margin: 16px auto"
              />
            </template>

            <template v-slot:foot()>{{ "" }}</template>
            <template v-slot:foot(totalWeight)>
              <span v-if="loaded">{{ formatWeight(bom.totalWeight) }}</span>
              <span v-else>-</span>
            </template>
            <template v-slot:foot(sum)>
              <span v-if="loaded">{{ formatPrice(bom.total) }}</span>
              <span v-else>-</span>
            </template>
            <template v-slot:foot(actions)>
              <b-button
                  class="downloadButton"
                  variant="outline-primary"
                  size="sm"
                  @click="downloadPdf()"
              >
                <font-awesome-icon
                    class="downloadIcon"
                    align="right"
                    icon="file-download"
                />
                <span class="ml-2">PDF</span>
              </b-button>
            </template>
          </b-table>
          <b-alert v-if="isUserInGermanSubsidiary && bom.total" show variant="info" class="text-center">
            <h5>
              {{ $t("order.discountedOnlinePriceTitle") }}
              <span
                  v-html="
                  $t('order.discountedOnlinePriceValueMsg', {
                    price: (bom.total * 0.98).toLocaleString($i18n.locale, {
                      style: 'currency',
                      currency: bom.currency,
                    }),
                  })
                "
              />
            </h5>
            <span
                class="small"
                v-html="$t('order.discountedOnlinePriceInfo')"
            />
          </b-alert>

          <column-configuration-delete-dialog
              ref="columnConfigurationDeleteDialog"
              :columnConfiguration="columnConfigurationToDelete"
              @confirm="deleteColumnConfiguration($event)"
          />
          <div class="sum-row button-row" v-if="!disabled">
            <b-container>
              <b-row>
                <b-col class="d-flex p-3">
                  <div class="weight-text-field"></div>
                  <div v-if="!disabled && loaded" class="order-button">
                    <b-button
                        class="request-button"
                        @click="$refs.requestModal.show()"
                        variant="primary"
                    >{{ $t("order.request") }}</b-button
                    >
                    <b-button
                        v-if="bom.total != null"
                        class="order-button"
                        variant="primary"
                        @click="$refs.orderModal.show()"
                    >{{ $t("order.order") }}</b-button
                    >
                  </div>
                </b-col>
              </b-row>
            </b-container>
          </div>

          <div class="notify">
            <div class="empty-module"></div>
            <div class="special-mediacolumn"></div>
          </div>

          <project-settings-dialog
              :show="$route.hash == '#edit'"
              @cancel="projectSubmitOrCancel"
              @submit="projectSubmitOrCancel"
          />

          <request-project-dialog
              ref="requestModal"
          />
          <order-project-dialog
              ref="orderModal"
          />
        </b-col>
      </b-row>
    </b-container>
  </div>
</template>

<script>
import columnConfigurationDeleteDialog from "../components/ColumnConfigurationDeleteDialog";
import projectSettingsDialog from "../components/ProjectSettingsDialog.vue";
import requestProjectDialog from "../components/RequestProjectDialog";
import orderProjectDialog from "../components/OrderProjectDialog";
import contactPopover from "../components/ContactPopover";
import formatNumbers from "../formatNumbers";
import { authContext } from "../auth";

export default {
  $_veeValidate: {
    validator: "new"
  },
  mixins: [formatNumbers],
  data() {
    return {
      startPdfDownload: false,
      dismissCountDown: 0,
      dismissSecs: 4,
      currentlyEdited: null,
      fields: [
        {
          key: "show_details",
          label: "",
          thStyle: "width: 2%"
        },
        {
          key: "name",
          label: this.$t("name")
        },
        {
          key: "changeableAmount",
          label: this.$t("configuration.quantity"),
          thStyle: "width: 10%"
        },
        {
          key: "configuration.height",
          label: this.$t("height"),
          thStyle: "width: 10%"
        },
        {
          key: "jibCrane",
          label: this.$t("configuration.jibCrane"),
          thStyle: "width: 10%"
        },
        {
          key: "mountingOrientation",
          label: this.$t("configuration.mountingOrientation.name"),
          thStyle: "width: 10%"
        },
        {
          key: "totalWeight",
          label: this.$t("bom.totalWeight"),
          thStyle: "width: 100px",
          tdClass: ["text-right"],
          thClass: ["text-right"]
        },
        {
          key: "sum",
          label: this.$t("bom.totalSum"),
          thStyle: "width: 100px",
          tdClass: ["text-right"],
          thClass: ["text-right"]
        },
        {
          key: "actions",
          label: "",
          thStyle: "width: 53.5px",
          tdClass: "text-center"
        }
      ],
      columnConfigurationToDelete: {}
    };
  },
  computed: {
    loaded() {
      return this.$store.state.projects.projectBom != null
    },
    isUserInGermanSubsidiary(){
      return authContext.user.subsidiary_code === "DE"
    },
    amounts() {
      return this.$store.state.configurations.configurationAmounts.map(
          c => c.amount
      );
    },
    items() {
      return (this.$store.state.configurations.configurationAmounts || []).map(
          x => {
            return { ...x };
          }
      );
    },
    bom() {
      return this.$store.state.projects.projectBom || [];
    },
    disabled: function() {
      if (!this.loaded) return false;
      return this.$store.getters["projects/disabled"];
    },
    names() {
      return this.$store.state.configurations.configurationAmounts.map(
          amount => amount.configuration.configurationName
      );
    },
    warnings() {
      const modules = this.$store.state.modules;
      if (!this.$store.state.configurations.configurationAmounts) return [];

      return this.$store.state.configurations.configurationAmounts.map((configuration) => {
        const warnings = new Set();
        const feedInCurrent = configuration.configuration.electricProtection
            ? configuration.configuration.electricProtection.feedInCurrent
            : 64;
        let somePowerModule = false;
        let feedIn = false;

        configuration.configuration.modulePlacements.forEach(placement => {
          const module = modules.find(
              module => module.itemNo == placement.itemNumber
          );
          if (!module) return;
          if (module.type == "TAPPING" && module.medium == "POWER")
            somePowerModule = true;

          if (
              (module.type == "FEEDING" && module.medium == "POWER") ||
              module.description == "Besen Kopfplatte" ||
              module.description == "Brush Headplate"
          )
            feedIn = true;

          if (
              module.metadata.amperage &&
              module.metadata.amperage > feedInCurrent &&
              configuration.configuration.electricProtection.rcd
          )
            warnings.add("tooHighElectricCurrent");
        });

        if (!feedIn && somePowerModule) warnings.add("noFeeding");

        return [...warnings]
      });
    }
  },
  components: {
    "column-configuration-delete-dialog": columnConfigurationDeleteDialog,
    "project-settings-dialog": projectSettingsDialog,
    "request-project-dialog": requestProjectDialog,
    "order-project-dialog": orderProjectDialog,
    "contact-popover": contactPopover
  },
  methods: {
    alertCountDownChanged(dismissCountDown) {
      this.dismissCountDown = dismissCountDown;
    },
    showAlert() {
      this.dismissCountDown = this.dismissSecs;
    },
    onEditNameClick(index) {
      if (this.currentlyEdited != index) {
        this.currentlyEdited = index;
        this.$nextTick(() => {
          this.$refs.nameInput.focus();
          this.$refs.nameInput.select();
        });
      } else {
        this.currentlyEdited = null;
      }
    },
    flatBom(bom) {
      if (bom == null) return [];
      const flatten = (item, depth = 0) => {
        const items = (item.entries || [])
            .map(x => flatten(x, depth + 1))
            .flat();
        return depth == 0
            ? items
            : [
              {
                description: item.description,
                price: this.formatPrice(item.totalPrice),
                weight: this.formatWeight(item.totalWeight),
                depth
              },
              ...items
            ];
      };
      return flatten(bom);
    },
    async downloadPdf() {
      this.startPdfDownload = true;
      this.showAlert();

      const url = await this.$store.dispatch("projects/generatePDF");
      const a = document.createElement("a");
      a.href = url;
      a.download = "base-config" + this.$store.state.projects.project.number + ".pdf";
      document.body.appendChild(a);
      a.click();
      a.remove();
    },
    checkIfEdited(myConfiguration) {
      if (this.currentlyEdited == myConfiguration) return false;
      this.$router.push({
        name: "modulePlacements",
        params: {
          configurationId: myConfiguration,
          projectId: this.$store.state.projects.project.number
        }
      });
    },
    changeConfigurationName(index, editedName) {
      if (editedName.trim().length == 0) {
        editedName = this.names[index];
      } else {
        editedName = editedName.trim();
        const nameRegex = /^(.+) \((\d+)\)$/;

        const names = this.$store.state.configurations.configurationAmounts
            .filter((_, i) => i != index)
            .map(conf => conf.configuration.configurationName);

        while (names.includes(editedName)) {
          let match = editedName.match(nameRegex);
          if (match) editedName = `${match[1]} (${+match[2] + 1})`;
          else editedName = `${editedName} (2)`;
        }
      }

      const amounts = this.$store.state.configurations.configurationAmounts
      this.$store.commit("configurations/changeConfigurationAmounts", amounts.map((amount, i) => {
        return i == index
            ? {
              ...amount,
              configuration: {
                ...amount.configuration,
                configurationName: editedName
              }
            }
            : amount
      }));
      this.$store.dispatch("configurations/saveConfiguration", index)
      this.currentlyEdited = null;
    },
    changeAmount(configIndex) {
      const amount =
          Math.round(Math.min(Math.max(this.amounts[configIndex], 1), 1000)) || 1;
      this.$set(this.amounts, configIndex, amount);
      const amounts = [
        ...this.$store.state.configurations.configurationAmounts
      ];
      if (amounts[configIndex].amount == amount) return;
      amounts[configIndex] = { ...amounts[configIndex], amount };
      this.$store.commit("configurations/changeConfigurationAmounts", amounts);
    },

    async createColumnConfiguration() {
      const id = await this.$store.dispatch("configurations/createNewConfiguration");
      this.$router.push({
        name: "columnTypesWithConfig",
        params: {
          projectId: this.$store.getters.projectNumber,
          configurationId: id
        }
      });
    },
    onDeleteClick(columnConf) {
      this.columnConfigurationToDelete = columnConf;
      this.$refs.columnConfigurationDeleteDialog.show();
    },
    async deleteColumnConfiguration(configAmountIndex) {
      await this.$store.state.configurations.configurationAmounts.splice(
          configAmountIndex,
          1
      );
      this.$store.dispatch("configurations/deleteConfiguration", configAmountIndex);
    },
    projectSubmitOrCancel() {
      this.$router.replace(
          this.$route.fullPath
              .toString()
              .substring(0, this.$route.fullPath.toString().indexOf("#edit"))
      );
    }
  }
};
</script>

<style lang="scss" scoped>
@import "../styles/common";

.notifications {
  left: 0;
  width: 100%;
  border-radius: 0px;
  z-index: 9;
}

.form-control:disabled {
  background-color: inherit;
}
.inlay {
  background-color: rgb(255, 150, 0);
  transform: translateX(-102%) translateY(-48px);
  border-radius: 6px;
  padding: 26px 76px;
  position: absolute;
}

.my-inner-tooltip .tooltip-inner {
  padding: 10px;
  max-width: 500px;
  text-align: left;
}

.help {
  z-index: 100;
  position: fixed;
  bottom: 0;
  left: 0;

  height: 6em;

  cursor: pointer;
}
.button-wrapper {
  display: flex;
  margin-top: 10px;
  justify-content: center;
}
.order-alert {
  text-align: center;
  font-size: initial;
  color: black;
}
.request-button {
  margin-right: 10px;
}
.order-button {
  margin-right: 10px;
}
.new-configuration-button {
  margin-top: 20px;
}
.show-details {
  color: $eepos-blue;
  cursor: pointer;
  transform: rotate(0deg);
  transition: all 200ms;
}
.show-details-open {
  color: $eepos-blue;
  cursor: pointer;
  transform: rotate(90deg);
  transition: all 200ms;
}
.sum-row {
  display: flex;
}
.button-row {
  border-top: 1px solid #dee2e6;
  position: fixed;
  bottom: 0;
  background: #f8f9fa;
  left: 0;
  width: 100%;
}
.weight-text-field {
  margin-left: 10px;
  flex-grow: 1;
  text-align: left;
  font-size: initial;
}
.sum-text-field {
  margin-left: 10px;
  flex-grow: 1;
  text-align: left;
  font-weight: bold;
  font-size: large;
}
.input-field {
  width: 100%;
  height: 130px;
}
.text-left {
  text-align: left;
}
.special-mediacolumn {
  width: 50%;
  align-self: auto;
  text-size-adjust: 9pt;
}
.notify {
  display: flex;
}
.empty-module {
  width: 50%;
}
.new-config-button {
  margin-left: 10px;
}
.order-button {
  -webkit-box-flex: 1;
  margin-right: 10px;
}
.column-configuration-table {
  margin-bottom: 10px;
  white-space: nowrap;
  color: #555555;
}
.configuration-link {
  color: $eepos-blue !important;
  text-align: left;
  border: none;
  background-color: transparent;
  cursor: pointer;
}
input[type="text"]:disabled {
  background: transparent;
  border: none;
  color: #00a0e4;
  cursor: pointer;
  display: inline;
}
input[type="text"] {
  display: inline;
}
input[type="number"] {
  background: transparent;
  border: none;
}
#contact-popover-container::v-deep .help {
  bottom: 75px !important;
}

.downloadIcon {
  color: $eepos-blue;
}

.downloadButton:hover {
  .downloadIcon {
    color: white;
  }
}
</style>

<style lang="scss">
.column-configuration-table {
  & thead th {
    border-top: none !important;
    vertical-align: baseline;
  }
  th,
  td,
  tr {
    text-align: left;
    vertical-align: middle !important;
  }
}
</style>
